<!--
  Buydown Calculator — ported from CGS2022 Scripts/Calculator/buydownCalculator.vue
  (Views/Calculators/BuydownCalculator.cshtml).

  WHAT CAME ACROSS
  The calculation, unchanged in method: 3-2-1 / 2-1 / 1-1 / 1-0 temporary buydowns, each year's
  P&I at the reduced rate on the full term, that year's monthly subsidy, and the buydown fund
  (the subsidies x 12, summed). The math lives in helpers/paymentMath.js (buydownSchedule) next
  to the pmt() the other calculators use, and it reproduces CGS's fund to the cent.

  WHAT DELIBERATELY DID NOT
  - Kendo Input / Button / DropDownList and the CGS dashboard chrome (drag handle, card CSS).
    Plain inputs on the calculator kit, so this matches the Payment Calculator.
  - The Calculate button. Figures update as you type, like every other calculator here.
  - CGS's whole-dollar rounding. Cents are shown; see the note in paymentMath.js.
  - CGS's "1-1" branch re-displayed year one's figures as year two. Equivalent output (both
    years are 1 point off), but here year two is simply computed as year two.
-->
<template>
  <div class="calc-page">
    <PageHeaderPortal>
      <div class="calc-header">
        <div class="calc-header__icon">
          <i class="fa-duotone fa-stairs"></i>
        </div>
        <div>
          <h1 class="calc-header__title">Buydown Calculator</h1>
          <p class="calc-header__sub">Temporary rate buydowns: year-by-year payments and the fund it takes</p>
        </div>
      </div>
    </PageHeaderPortal>

    <div class="calc-body">
      <section class="page-card step-card">
        <div class="step-head">
          <span class="step-icon tone-amber"><i class="fa-light fa-stairs"></i></span>
          <div class="step-head-text">
            <p class="eyebrow">Loan Terms</p>
            <h2>Price the buydown</h2>
            <p class="step-desc">
              Pick the buydown, then enter the loan. Each bought-down year is priced at the reduced
              rate over the full term; the gap to the note-rate payment is what the fund covers.
            </p>
          </div>
        </div>

        <div class="bd-toggles">
          <span class="field-label">Buydown type</span>
          <div class="seg">
            <button v-for="t in BUYDOWN_TYPES" :key="t" type="button"
                    class="seg__btn" :class="{ 'is-active': type === t }"
                    @click="type = t">
              {{ t }}
            </button>
          </div>
        </div>

        <div class="field-grid">
          <label class="field">
            <span class="field-label">Loan amount</span>
            <span class="input-shell">
              <span class="affix affix--pre">$</span>
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="loanAmount" @blur="loanAmount = money2(loanAmount)" />
            </span>
          </label>

          <label class="field">
            <span class="field-label">Note rate</span>
            <span class="input-shell">
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="noteRatePct" @blur="noteRatePct = rate3(noteRatePct)" />
              <span class="affix affix--post">%</span>
            </span>
          </label>

          <label class="field">
            <span class="field-label">Term</span>
            <span class="input-shell">
              <input type="text" inputmode="numeric" autocomplete="off" v-model="term" />
              <span class="affix affix--post">months</span>
            </span>
          </label>
        </div>

        <div v-if="calc.error" class="calc-note calc-note--error bd-error">
          <i class="fa-duotone fa-triangle-exclamation"></i>
          <div>
            <strong>That buydown doesn't fit the rate</strong>
            {{ calc.error }}
          </div>
        </div>

        <div class="step-foot">
          <button type="button" class="secondary-button" @click="resetAll">Reset</button>
          <div class="foot-right">
            <button type="button" class="secondary-button" :disabled="!calc.isPriced" @click="copySummary">
              {{ copied ? 'Copied' : 'Copy Summary' }}
            </button>
          </div>
        </div>
      </section>

      <aside class="rail--dark">
        <div>
          <p class="eyebrow eyebrow--gold">Buydown Fund</p>
          <h3>{{ type }} buydown</h3>
        </div>

        <div class="rail-hero">
          <span>Funds needed</span>
          <strong>{{ calc.isPriced ? money(calc.buydownFund) : '&mdash;' }}</strong>
        </div>

        <dl class="rail-rows">
          <div v-for="y in calc.years" :key="y.year">
            <dt>Year {{ y.year }} &middot; {{ pct(y.rate) }}</dt>
            <dd>{{ calc.isPriced ? money(y.payment) : '&mdash;' }}</dd>
          </div>
        </dl>

        <div class="rail-highlight">
          <span>Year {{ calc.years.length + 1 }} on &middot; {{ pct(num(noteRatePct)) }}</span>
          <strong>{{ calc.isPriced ? money(calc.fullPayment) : '&mdash;' }}</strong>
        </div>
      </aside>
    </div>

    <section v-if="calc.isPriced" class="page-card">
      <div class="step-head">
        <span class="step-icon tone-slate"><i class="fa-light fa-table-list"></i></span>
        <div class="step-head-text">
          <p class="eyebrow">Schedule</p>
          <h2>Year by year</h2>
          <p class="step-desc">
            Principal and interest only. Taxes, insurance and MI are the same in every year and
            are not part of the buydown.
          </p>
        </div>
      </div>

      <div class="bd-table">
        <table class="calc-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Rate</th>
              <th>Monthly P&amp;I</th>
              <th>Monthly subsidy</th>
              <th>Year's subsidy</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="y in calc.years" :key="y.year">
              <td>{{ y.year }}</td>
              <td>{{ pct(y.rate) }}</td>
              <td>{{ money(y.payment) }}</td>
              <td>{{ money(y.subsidy) }}</td>
              <td>{{ money(y.annualSubsidy) }}</td>
            </tr>
            <tr>
              <td>{{ calc.years.length + 1 }}+</td>
              <td>{{ pct(num(noteRatePct)) }}</td>
              <td>{{ money(calc.fullPayment) }}</td>
              <td>&mdash;</td>
              <td>&mdash;</td>
            </tr>
            <tr class="bd-total">
              <td colspan="4">Buydown fund</td>
              <td>{{ money(calc.buydownFund) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
  import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
  import { BUYDOWN_TYPES, buydownSchedule, num, hasValue, money, pct } from "@/helpers/paymentMath.js"

  export default {
    name: "BuydownCalculator",
    components: { PageHeaderPortal },

    data() {
      return {
        BUYDOWN_TYPES,
        type: "3-2-1",
        loanAmount: "",
        // A PERCENT (7.500), like everything in paymentMath.
        noteRatePct: "",
        term: "360",
        copied: false
      }
    },

    computed: {
      calc() {
        return buydownSchedule({
          loanAmount: this.loanAmount,
          noteRatePct: this.noteRatePct,
          termMonths: this.term,
          type: this.type
        })
      }
    },

    methods: {
      num,
      money,
      pct,

      money2(raw) {
        if (!hasValue(raw)) return ""
        return num(raw).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      },
      rate3(raw) {
        if (!hasValue(raw)) return ""
        return num(raw).toFixed(3)
      },

      resetAll() {
        Object.assign(this, { type: "3-2-1", loanAmount: "", noteRatePct: "", term: "360", copied: false })
      },

      async copySummary() {
        const l = [
          `Buydown Calculator — ${this.type}`,
          "",
          `  Loan amount: ${money(num(this.loanAmount))}`,
          `  Note rate: ${pct(num(this.noteRatePct))} for ${Math.round(num(this.term))} months`,
          ""
        ]
        for (const y of this.calc.years) {
          l.push(`  Year ${y.year} (${pct(y.rate)}): ${money(y.payment)} P&I, subsidy ${money(y.subsidy)}/mo, ${money(y.annualSubsidy)}/yr`)
        }
        l.push(`  Year ${this.calc.years.length + 1} on (${pct(num(this.noteRatePct))}): ${money(this.calc.fullPayment)} P&I`)
        l.push("", `  Buydown fund: ${money(this.calc.buydownFund)}`)
        try {
          await navigator.clipboard.writeText(l.join("\n"))
          this.copied = true
          setTimeout(() => (this.copied = false), 2000)
        } catch (e) {
          this.copied = false
        }
      }
    }
  }
</script>

<style scoped>
  /* Shared styling is in assets/css/calculatorKit.css; only this page's layout is here. */
  .bd-toggles {
    display: flex;
    flex-direction: column;
    gap: 7px;
    margin-top: 22px;
  }

  .bd-error {
    margin-top: 20px;
  }

  .bd-table {
    margin-top: 18px;
    border: 1px solid var(--calc-line);
    border-radius: 9px;
    overflow: auto;
  }

  .bd-total td {
    font-weight: 700;
  }
</style>
